import { FunctionComponent, useCallback, useState } from "react";

import { Grid } from "@mui/material";
import { EmailContentType } from "@simplyk/common";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { EmailEditorHeader } from "./Components/EmailEditorHeader";
import { EmailEditor, EmailLoader } from "./EmailEditor";
import { useEmailPreview } from "./hooks/useEmailPreview";
import { useGetEmailContent } from "./useGetEmailContent";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";

export type EmailEditorDrawerProps = {
  emailContentId?: string;
  closeEmailEditor: () => void;
  isOpen: boolean;
  onEmailGenerated?: (transactionId: string, body: string) => void;
};

export const EmailEditorDrawer: FunctionComponent<React.PropsWithChildren<EmailEditorDrawerProps>> = (props) => {
  const router = useRouter();
  const pathname = usePathname();
  const { logAmplitudeEvent } = useAmplitude();
  const { t } = useTranslate();

  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const thankYouTransactionId = searchParams.get("thankYouTransactionId");
  const utils = trpc.useUtils();
  const { mutateAsync: deleteDraftEmailContentMutation } = trpc.automaticDeleteDraftEmailContent.useMutation();

  const {
    emailContent,
    isLoading: emailContentIsLoading,
    refetchEmailContent,
    // A closed drawer must not fetch: on draft-carrying routes it would cache the row pre-generation,
    // and the full-screen editor would then open on that stale designConfig-less row.
  } = useGetEmailContent(props.isOpen ? props.emailContentId : undefined);
  const isLoading = !emailContent || emailContentIsLoading;

  const [recipientsCount, setRecipientsCount] = useState(0);
  const { previewOpened, openPreview, closePreview } = useEmailPreview({ emailContent });

  const handleCloseDrawer = useCallback(async () => {
    logAmplitudeEvent(AmplitudeEvents.EmailEditorClickClose, {
      openFrom: emailContent?.entryPoint ?? emailContent?.openedFrom,
    });

    if (emailContent?.id && !emailContent.isAutomaticallyGenerated) {
      void deleteDraftEmailContentMutation({ emailContentId: emailContent.id })
        .then(() => utils.getCommunicationEmailList.invalidate())
        .catch(() => undefined);
    }

    props.closeEmailEditor();

    const deliveryId = searchParams.get("deliveryId");
    if (deliveryId) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { deliveryId, from, showTour, ...params } = query;
      router.push(`${pathname}?${new URLSearchParams(params as Record<string, string>).toString()}`);
    }
  }, [
    props,
    searchParams,
    logAmplitudeEvent,
    emailContent?.entryPoint,
    emailContent?.openedFrom,
    emailContent?.id,
    emailContent?.isAutomaticallyGenerated,
    deleteDraftEmailContentMutation,
    utils.getCommunicationEmailList,
    query,
    router,
    pathname,
  ]);

  return (
    <>
      <DashboardDrawer
        open={props.isOpen}
        onClose={handleCloseDrawer}
        slots={{ header: <></> }}
        width={previewOpened ? "90%" : undefined}
        noPadding
        disableEnforceFocus
      >
        <div style={{ height: "100%" }} data-test="email-editor-drawer">
          <Grid container sx={{ height: "100%" }} paddingTop={2} paddingBottom={2} direction="column">
            <Grid
              item
              marginBottom={{ xs: 3, md: 5 }}
              paddingLeft={{ xs: 2.5, md: 2 }}
              paddingRight={{ xs: 2.5, md: 2 }}
              sx={(theme) => ({
                paddingBottom: { xs: 2, md: 0 },
                borderBottom: { xs: `1px solid ${theme.palette.border.neutral.quiet}`, md: "none" },
              })}
            >
              <EmailEditorHeader
                handleClose={handleCloseDrawer}
                title={
                  emailContent?.emailType === EmailContentType.CRMWORKFLOW
                    ? t("dashboard", "emailEditor.createYourEmail")
                    : undefined
                }
                recipientsCount={recipientsCount}
                previewOpened={previewOpened}
                openPreview={openPreview}
                closePreview={closePreview}
                emailType={emailContent?.emailType}
                hidePreviewButton={
                  emailContent?.emailType === EmailContentType.EXTRA_THANK_YOU_EMAIL ||
                  emailContent?.emailType === EmailContentType.INCOMPLETE_PAYMENT_FOLLOW_UP ||
                  emailContent?.emailType === EmailContentType.CRMWORKFLOW
                }
              />
            </Grid>
            <Grid item xs style={{ maxWidth: "100%", overflowY: "auto" }}>
              {isLoading || (!emailContent && <EmailLoader />)}
              {!isLoading && emailContent && (
                <EmailEditor
                  emailContent={emailContent}
                  onClose={props.closeEmailEditor}
                  refetch={refetchEmailContent}
                  previewOpened={previewOpened}
                  closePreview={closePreview}
                  setRecipientsCount={setRecipientsCount}
                  onEmailGenerated={
                    thankYouTransactionId
                      ? (body) => {
                          props.onEmailGenerated?.(thankYouTransactionId, body);
                        }
                      : undefined
                  }
                />
              )}
            </Grid>
          </Grid>
        </div>
      </DashboardDrawer>
    </>
  );
};
